import React, { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import classnames from 'classnames';
import { State } from 'state';
import Modal from 'utils/components/modal';
import {
  ILiveBlogState,
  LiveBlogFilter,
  LiveBlogSorting,
  LiveEvent,
  LiveBlogModalMode
} from '../../state/types';
import SpacerVertical from '../../../../common/spacer-vertical';
import PaginationBox from '../../../../common/pagination-box/pagination-box';
import { getLiveBlogEvents, getLiveBlogSettings, changePageSettings } from '../../state/reducer';
import { deleteLiveBlogEvent, fetchLiveBlogEvents } from '../../state/actions';
import LiveBlogModal from '../Modal';
import ConfirmModal from '../../../../modals/confirm';
import EmptyList from './EmptyList';
import EventListHeader from './Header';
import EventListItem from './Item';

const EventList: React.FC = () => {
  const [selectedEvent, setSelectedEvent] = useState<LiveEvent>();
  const [mode, setMode] = useState<LiveBlogModalMode>();
  const modalRef = useRef();
  const deleteModalRef = useRef();
  const dispatch = useDispatch();
  const { liveEvents, eventCount } = useSelector<
    State,
    { liveEvents: LiveEvent[]; eventCount: number }
  >(getLiveBlogEvents);
  const { timeFilter, pageNum, sortBy, isLoading } = useSelector<State, Partial<ILiveBlogState>>(
    getLiveBlogSettings
  );

  useEffect(() => {
    dispatch(fetchLiveBlogEvents());
  }, []);

  const onPageChange = (pageNum: number) => {
    dispatch(changePageSettings({ pageNum }));
  };

  const onTimeFilterChange = (timeFilter: LiveBlogFilter) => {
    dispatch(changePageSettings({ timeFilter }));
  };

  const onSortingChange = (sortBy: LiveBlogSorting) => {
    dispatch(changePageSettings({ sortBy }));
  };

  const openModal = (mode: LiveBlogModalMode = LiveBlogModalMode.Create, eventId?: string) => {
    const selectedEvent = liveEvents.find((event: LiveEvent) => eventId === event.id);
    setSelectedEvent(selectedEvent);
    setMode(mode);
    // @ts-ignore
    modalRef?.current?.show();
  };

  const closeModal = () => {
    // @ts-ignore
    modalRef?.current?.hide();
    setSelectedEvent(null);
  };

  const openDeleteModal = (eventId: string) => {
    const selectedEvent = liveEvents.find((event: LiveEvent) => eventId === event.id);
    // @ts-ignore
    deleteModalRef?.current?.show();
    setSelectedEvent(selectedEvent);
  };

  const closeDeleteModal = () => {
    // @ts-ignore
    deleteModalRef?.current?.hide();
    setSelectedEvent(null);
  };

  const onConfirmDeleteModal = () => {
    onDelete(selectedEvent.id);
    closeDeleteModal();
  };

  const onEdit = (eventId: string) => {
    openModal(LiveBlogModalMode.Edit, eventId);
  };

  const onCreate = () => {
    openModal(LiveBlogModalMode.Create);
  };

  const onDuplicate = (eventId: string) => {
    openModal(LiveBlogModalMode.Duplicate, eventId);
  };

  const onDelete = (eventId: string) => {
    dispatch(deleteLiveBlogEvent({ eventId }));
  };

  return (
    <>
      <EventListHeader
        timeFilter={timeFilter}
        sortBy={sortBy}
        count={eventCount}
        onTimeFilterChange={onTimeFilterChange}
        onSortingChange={onSortingChange}
        onCreate={onCreate}
      />
      <ul className={classnames('live-events-list', { ['no-border']: liveEvents.length === 0 })}>
        {liveEvents.length ? (
          liveEvents.map(liveEvent => (
            <EventListItem
              key={liveEvent.eventCode}
              liveEvent={liveEvent}
              onEdit={onEdit}
              onDuplicate={onDuplicate}
              onDelete={openDeleteModal}
            />
          ))
        ) : (
          <EmptyList
            isLoading={isLoading}
            timeFilter={timeFilter}
            onTimeFilterChange={onTimeFilterChange}
          />
        )}
      </ul>
      <PaginationBox
        totalRecords={eventCount}
        offset={(pageNum - 1) * 10}
        onPageChanged={onPageChange}
        itemsPerPage={10}
      />
      <Modal
        className={'live-blog-modal-wrapper'}
        closeClassName={'live-blog-modal-close-button'}
        ref={modalRef}
      >
        <LiveBlogModal event={selectedEvent} mode={mode} closeModal={closeModal} />
      </Modal>
      <Modal
        ref={deleteModalRef}
        className={classnames('confirm-modal', 'live-blog-modal-wrapper', 'delete-modal')}
      >
        <ConfirmModal
          title="Delete Event"
          onClose={closeDeleteModal}
          onConfirm={onConfirmDeleteModal}
          description={'This event will be deleted. Are you sure?'}
          buttonName={'Delete'}
        />
      </Modal>

      <SpacerVertical size={'80px'} />
    </>
  );
};

export default EventList;
